import { PaddingSystemProps } from '@rover/kibble/core/styledSystemPropTypes';

import type { DialogButtonProps } from '../Modal/Modal.common';

/** Common props for BottomSheet across web and native. */
export type BottomSheetCommonProps = DialogButtonProps & {
  /** The body. */
  children: React.ReactNode;
  /** Shows the sheet. It slides up when this turns `true` and back down when it turns `false`. */
  isOpen: boolean;
  /** The sheet's heading, styled to match Figma. Takes the place of `customHeader`. */
  title?: string;
  /** A line of text under the title. */
  subtitle?: string;
  /**
   * Fills the screen with square corners and shows a ✕ in place of the drag handle. Web scrolls
   * the whole sheet; native scrolls the body and ignores `height`.
   */
  isFullHeight?: boolean;
  /**
   * Overrides whether the ✕ shows. Figma shows it only on full-height sheets, which happens when
   * this is unset, so set it only when a sheet can't follow the design.
   */
  showCloseIcon?: boolean;
  /** @deprecated Use `showCloseIcon={false}`. `showCloseIcon` wins when both are set. */
  hideCloseIcon?: boolean;
  /**
   * Shows a drag handle; a drag down or a press on the handle calls `onRequestClose`. Defaults to
   * `true`. `false` shows the ✕ instead, which Figma reserves for `isFullHeight` sheets.
   */
  closeOnDragDown?: boolean;
  /**
   * Lets a press on the scrim, Escape, a drag down or Android's Back close the sheet, and shows the
   * handle. Defaults to `true`. With `false`, Escape and Back still work while the ✕ shows.
   */
  isDismissible?: boolean;
  /**
   * Padding for the body, title and subtitle, and on web the header row and footer. Defaults to
   * `{ paddingX: '4x' }`, which a value replaces rather than extends.
   */
  contentPaddingStyles?: Partial<PaddingSystemProps>;
  /**
   * Called when the reader asks to close the sheet. Set `isOpen` to `false` here, or the web sheet
   * stays open and the React Native one can't reopen.
   */
  onRequestClose: (payload?: unknown) => void;
  /**
   * Use only when the dialog button props can't cover your actions, such as a destructive button
   * or an inline error. Renders below the buttons. Web keeps it outside the scroll area; native
   * scrolls it with the body.
   */
  customFooter?: React.ReactNode;
  /**
   * Use only when `title` and `subtitle` can't cover your header. Sits beside the ✕, and is
   * ignored when `title` is set.
   */
  customHeader?: React.ReactNode;
  /**
   * Native-only fixed height. Array is [xxs, xs, sm, md, lg]. A fixed height doesn't scroll, so
   * content past it is cut off. To cap the sheet and scroll past the cap, omit this and set
   * `maxHeight`. To keep a fixed size, wrap the body in a `ScrollView` and set `dragFromTopOnly`.
   */
  height?: number | `${number}%` | number[];
  /**
   * Native-only; web only drags by the handle. Limits dragging to the top of the sheet. Defaults
   * to `false`. If the body holds any scrolling list (`ScrollView`, `FlatList` or similar), check
   * it scrolls on both iOS and Android, since the sheet's drag can take the swipe, and set this if
   * it doesn't.
   */
  dragFromTopOnly?: boolean;
  /**
   * Cap on the sheet's content. Web takes any CSS length and defaults to 80vh; native takes a
   * number, `n%` or `nvh` of the window and defaults to 90% of its container.
   */
  maxHeight?: number | string;
};

/** Default horizontal padding for bottom sheet content */
export const defaultContentPaddingStyles: Partial<PaddingSystemProps> = { paddingX: '4x' };

/** Which close control the sheet shows: a drag handle, a ✕, or neither. */
export const getDismissControls = ({
  isFullHeight,
  closeOnDragDown,
  hideCloseIcon,
  showCloseIcon = hideCloseIcon ? false : undefined,
  isDismissible,
}: Required<Pick<BottomSheetCommonProps, 'isFullHeight' | 'closeOnDragDown' | 'isDismissible'>> &
  Pick<BottomSheetCommonProps, 'showCloseIcon' | 'hideCloseIcon'>): {
  showHandle: boolean;
  showCloseIcon: boolean;
} => ({
  showHandle: isDismissible && closeOnDragDown && !isFullHeight,
  showCloseIcon: showCloseIcon ?? (isDismissible && (isFullHeight || !closeOnDragDown)),
});

/** Props for dismiss button component */
export type DismissButtonProps = Pick<BottomSheetCommonProps, 'onRequestClose'>;
